import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Image, {StaticImageData} from 'next/image';
import {cdnImage} from '@/util/cdnImage';
import {fontSize, borderRadius, shadows} from '@/styles/tokens';
import {imgSizes} from '@/util/imageSizes';

const img1 = cdnImage('about-us-new/Culture-image-1.webp');
const img2 = cdnImage('about-us-new/Culture-image-2.webp');
const img3 = cdnImage('about-us-new/Culture-image-3.webp');
const img4 = cdnImage('about-us-new/Culture-image-4.webp');
const img5 = cdnImage('about-us-new/Culture-image-5.webp');
const img6 = cdnImage('about-us-new/Culture-image-6.webp');
const img7 = cdnImage('about-us-new/Culture-image-7.webp');
const img8 = cdnImage('about-us-new/Culture-image-8.webp');
const img9 = cdnImage('about-us-new/Culture-image-9.webp');
const img10 = cdnImage('about-us-new/Culture-image-10.webp');
const img11 = cdnImage('about-us-new/Culture-image-11.webp');
const img12 = cdnImage('about-us-new/Culture-image-12.webp');
const img13 = cdnImage('about-us-new/Culture-image-13.webp');
const img14 = cdnImage('about-us-new/Culture-image-14.webp');
const img15 = cdnImage('about-us-new/Culture-image-15.webp');
const img16 = cdnImage('about-us-new/Culture-image-16.webp');
const img17 = cdnImage('about-us-new/Culture-image-17.webp');
const img18 = cdnImage('about-us-new/Culture-image-18.webp');
const img19 = cdnImage('about-us-new/Culture-image-19.webp');
const img20 = cdnImage('about-us-new/Culture-image-20.webp');
const img21 = cdnImage('about-us-new/Culture-image-21.webp');
const img22 = cdnImage('about-us-new/Culture-image-22.webp');
const img23 = cdnImage('about-us-new/Culture-image-23.webp');
const img24 = cdnImage('about-us-new/Culture-image-24.webp');
const img25 = cdnImage('about-us-new/Culture-image-25.webp');
const img26 = cdnImage('about-us-new/Culture-image-26.webp');
const img27 = cdnImage('about-us-new/Culture-image-27.webp');
const img28 = cdnImage('about-us-new/Culture-image-28.webp');
const img29 = cdnImage('about-us-new/Culture-image-29.webp');
const img30 = cdnImage('about-us-new/Culture-image-30.webp');
const img31 = cdnImage('about-us-new/Culture-image-31.webp');
const img32 = cdnImage('about-us-new/Culture-image-32.webp');
const img33 = cdnImage('about-us-new/Culture-image-33.webp');
const img34 = cdnImage('about-us-new/Culture-image-34.webp');
const img35 = cdnImage('about-us-new/Culture-image-35.webp');
const img36 = cdnImage('about-us-new/Culture-image-36.webp');

const allImages: StaticImageData[] = [
    img1,
    img2,
    img3,
    img4,
    img5,
    img6,
    img7,
    img8,
    img9,
    img10,
    img11,
    img12,
    img13,
    img14,
    img15,
    img16,
    img17,
    img18,
    img19,
    img20,
    img21,
    img22,
    img23,
    img24,
    img25,
    img26,
    img27,
    img28,
    img29,
    img30,
    img31,
    img32,
    img33,
    img34,
    img35,
    img36
];

const IMAGES_PER_SLIDE = 6;

// 6 bento grid slides, doubled for seamless loop
const slides = Array.from({length: 6}, (_, i) => allImages.slice(i * IMAGES_PER_SLIDE, (i + 1) * IMAGES_PER_SLIDE));
const doubledSlides = [...slides, ...slides];

// Bento grid: 'a'/'b'/'e'/'f' are the wide tiles, 'c'/'d' the half-width ones.
const wideTileSizes = imgSizes({xs: '160px', sm: '300px', md: '490px'});
const narrowTileSizes = imgSizes({xs: '80px', sm: '150px', md: '245px'});

const tileConfig = [
    {area: 'a', sizes: wideTileSizes},
    {area: 'b', sizes: wideTileSizes},
    {area: 'c', sizes: narrowTileSizes},
    {area: 'd', sizes: narrowTileSizes},
    {area: 'e', sizes: wideTileSizes},
    {area: 'f', sizes: wideTileSizes}
];

const BentoGrid = ({images, idx}: {images: StaticImageData[]; idx: number}) => (
    <Box
        sx={{
            display: 'grid',
            minWidth: 'var(--slide-w)',
            width: 'var(--slide-w)',
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gridTemplateRows: 'var(--row1) var(--row2) var(--row3)',
            gap: 1.5,
            gridTemplateAreas: `
                "a a b b"
                "a a c d"
                "e e f f"
            `
        }}
    >
        {images.map((img, i) => (
            <Box
                key={i}
                sx={{
                    gridArea: tileConfig[i].area,
                    position: 'relative',
                    borderRadius: borderRadius.lg,
                    overflow: 'hidden',
                    boxShadow: shadows.lg,
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    '& img': {transition: 'transform 0.4s ease'},
                    '&:hover img': {transform: 'scale(1.05)'}
                }}
            >
                <Image
                    src={img}
                    alt={`Debut culture ${idx * IMAGES_PER_SLIDE + i + 1}`}
                    fill
                    style={{objectFit: 'cover'}}
                    sizes={tileConfig[i].sizes}
                />
            </Box>
        ))}
    </Box>
);

const CultureSection = () => {
    return (
        <Box>
            <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                <Typography variant="h1" component="h2" mb={3} fontSize={{xs: fontSize[32], sm: fontSize[48]}} className="font-headers">
                    The Culture Behind the Work
                </Typography>
            </Box>

            {/* Continuously scrolling bento grids */}
            <Box
                sx={{
                    overflow: 'hidden',
                    // CSS variables drive both slide sizing and the keyframe distance, so layout + animation stay in sync per breakpoint
                    '--slide-w': '320px',
                    '--row1': '120px',
                    '--row2': '80px',
                    '--row3': '110px',
                    '--gap': '14px',
                    '--total-w': 'calc((var(--slide-w) + var(--gap)) * 6)',
                    '@media (min-width: 600px)': {
                        '--slide-w': '600px',
                        '--row1': '180px',
                        '--row2': '120px',
                        '--row3': '160px'
                    },
                    '@media (min-width: 900px)': {
                        '--slide-w': '980px',
                        '--row1': '260px',
                        '--row2': '180px',
                        '--row3': '240px'
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 'var(--gap)',
                        width: 'max-content',
                        willChange: 'transform',
                        animation: 'scroll-grids 60s linear infinite',
                        '&:hover': {animationPlayState: 'paused'},
                        '@keyframes scroll-grids': {
                            '0%': {transform: 'translate3d(0, 0, 0)'},
                            '100%': {transform: 'translate3d(calc(var(--total-w) * -1), 0, 0)'}
                        }
                    }}
                >
                    {doubledSlides.map((slideImages, i) => (
                        <BentoGrid key={i} images={slideImages} idx={i % 6} />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default memo(CultureSection);
